/**
 * Full-size media viewer for a flagged submission.
 *
 * Content is blurred by default and requires an explicit "View content"
 * click to reveal — this is a deliberate safety default, not just a UX
 * flourish. We don't know the age range of this app's users, and a
 * challenge creator reviewing a flag isn't necessarily an adult, so
 * nothing sensitive is shown automatically anywhere in this flow. The
 * reveal state resets every time the lightbox is opened.
 *
 * Has an X button to close, and also closes on Escape or click-outside.
 */

import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Submission } from '@jemmi/schemas';

import { cn } from '@/lib/cn';

type Props = {
  submission: Submission;
  onClose: () => void;
};

export function MediaLightbox({ submission, onClose }: Props) {
  const [revealed, setRevealed] = useState(false);

  // Close on Escape.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className="relative flex max-h-full max-w-3xl items-center justify-center overflow-hidden rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video only mounts (and can autoplay) after the reveal step —
            never load/play media before the user has explicitly opted in. */}
        {revealed && submission.mediaType === 'video' ? (
          <video
            src={submission.mediaUrl}
            controls
            autoPlay
            className="max-h-[85vh] max-w-full rounded-lg"
          />
        ) : (
          <img
            src={submission.mediaUrl}
            alt={submission.caption || 'Submission'}
            className={cn(
              'max-h-[85vh] max-w-full rounded-lg object-contain transition-[filter] duration-200',
              !revealed && 'scale-105 blur-2xl',
            )}
          />
        )}

        {!revealed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-white" />
            <p className="max-w-xs text-sm font-medium text-white">
              This submission was flagged by the community and may contain sensitive content.
            </p>
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
            >
              View content
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
